import * as Yup from 'yup';
import { SchemaLike } from 'yup/lib/types';
import { getRequiredText } from '../helpers/transformer';
import { RegularExpressions } from '../constants/regular-expressions';
import { LabelSettingProps } from '../models/epi-model';
import { YesNoIndicators } from '../models/api-model';

const getFieldNameWithPrefix = (addressType: string, fieldName: string) => {
	const prefix = addressType.length ? `${addressType} ` : '';

	return `${prefix}${fieldName}`;
};

export const address_1_validation = (labelSetting: LabelSettingProps, addressType: string) => {
	const fieldNameWithPrefix = getFieldNameWithPrefix(addressType, labelSetting.addressLabel1);

	return Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, fieldNameWithPrefix)).test(
		'empty-or-2-characters-check',
		labelSetting.min2CharacterErrorMessage,
		value => !value || value?.replace(/\s+/g, '')?.length >= 2,
	).test(
		'at-least-one-letter-or-empty',
		labelSetting.mustContainAlphabeticalErrorMessage,
		(value) => {
			return !value || /[a-zA-Z]/.test(value);
		});
};

const validation_nonepiscopal = (thenSchema: SchemaLike, otherwiseSchema?: SchemaLike) => {
	return Yup.string().ensure().when(['nonEpiscopal'],
		{
			is: YesNoIndicators.Yes,
			then: thenSchema,
			otherwise: otherwiseSchema,
		});
};

export const address_2_validation = (labelSetting: LabelSettingProps) => {
	return Yup.string().nullable()
		.matches(RegularExpressions.SpecialCharacters,
			labelSetting.specialCharactersError || 'No special characters').max(30,
				labelSetting.maxCharactersError?.replace('{max}', '30') || 'Maximum 30 characters').test(
					'empty-or-2-characters-check',
					labelSetting.min2CharacterErrorMessage,
					value => !value || value?.replace(/\s+/g, '')?.length >= 2,
				).test(
					'at-least-one-letter-or-empty',
					labelSetting.mustContainAlphabeticalErrorMessage,
					(value) => {
						return !value || /[a-zA-Z]/.test(value);
					});
};
export const address_3_validation = (labelSetting: LabelSettingProps) => {

	return Yup.string().nullable().matches(RegularExpressions.SpecialCharacters,
		labelSetting.specialCharactersError || 'No special characters').test(
			'empty-or-2-characters-check',
			labelSetting.min2CharacterErrorMessage,
			value => !value || value?.replace(/\s+/g, '')?.length >= 2,
		).test(
			'at-least-one-letter-or-empty',
			labelSetting.mustContainAlphabeticalErrorMessage,
			(value) => {
				return !value || /[a-zA-Z]/.test(value);
			});
};

export const country_validation = (labelSetting: LabelSettingProps, addressType: string) => {
	const fieldNameWithPrefix = getFieldNameWithPrefix(addressType, labelSetting.countryLabel);

	return Yup.string().required(getRequiredText(labelSetting.requiredFormat, `${fieldNameWithPrefix}`));
};

export const city_validation = (labelSetting: LabelSettingProps, addressType: string) => {
	const fieldNameWithPrefix = getFieldNameWithPrefix(addressType, labelSetting.cityLabel);

	return Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, `${fieldNameWithPrefix}`)).test(
		'empty-or-2-characters-check',
		labelSetting.min2CharacterErrorMessage,
		value => !value || value?.replace(/\s+/g, '')?.length >= 2,
	).test(
		'at-least-one-letter-or-empty',
		labelSetting.mustContainAlphabeticalErrorMessage,
		(value) => {
			return !value || /[a-zA-Z]/.test(value);
		});
};

export const state_validation = (labelSetting: LabelSettingProps, countryFieldName: string, addressType: string) => {
	const stateLabelWithPrefix = getFieldNameWithPrefix(addressType, labelSetting.stateLabel);
	const provinceLabelWithPrefix = getFieldNameWithPrefix(addressType, labelSetting.provinceLabel);


	return Yup.string().nullable().ensure().when(countryFieldName,
		{
			is: 'US',
			then: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, `${stateLabelWithPrefix}`)),
		}).ensure().when(countryFieldName,
			{
				is: 'CA',
				then: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, `${provinceLabelWithPrefix}`)),
			}).ensure().when(countryFieldName,
				{
					is: 'MX',
					then: Yup.string().nullable().required(getRequiredText(labelSetting.requiredFormat, `${provinceLabelWithPrefix}`)),
				});
};

export const postal_code_validation = (labelSetting: LabelSettingProps, countryFieldName: string, addressType: string) => {
	const fieldNameWithPrefix = getFieldNameWithPrefix(addressType, labelSetting.postalLabel);

	return Yup.string().nullable().ensure().when(
		countryFieldName,
		{
			is: 'US',
			then: Yup.string().nullable()
				.matches(RegularExpressions.USPostalCode, `${labelSetting.invalidError || 'Invalid'} ${fieldNameWithPrefix}`),
		}).ensure().when(countryFieldName,
			{
				is: 'CA',
				then: Yup.string().nullable()
					.matches(RegularExpressions.CAPostalCode, `${labelSetting.invalidError || 'Invalid'} ${fieldNameWithPrefix}`),
			}).ensure().when(countryFieldName,
				{
					is: 'MX',
					then: Yup.string().nullable()
						.matches(RegularExpressions.MXPostalCode, `${labelSetting.invalidError || 'Invalid'} ${fieldNameWithPrefix}`),
				});
};

export const PersonInfoMailingAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = '';

	return {
		mailing_address_1: address_3_validation(labelSetting),
		mailing_address_2: address_2_validation(labelSetting),
		mailing_address_3: address_3_validation(labelSetting),
		mailing_city: Yup.string().nullable().test(
			'empty-or-2-characters-check',
			labelSetting.min2CharacterErrorMessage,
			value => !value || value?.replace(/\s+/g, '')?.length >= 2,
		).test(
			'at-least-one-letter-or-empty',
			labelSetting.mustContainAlphabeticalErrorMessage,
			(value) => {
				return !value || /[a-zA-Z]/.test(value);
			}),
		mailing_state: state_validation(labelSetting, 'mailing_country', addressType),
		mailing_postal_code: postal_code_validation(labelSetting, 'mailing_country', addressType),
	};
};

export const ClergyBioAddressValidationSchema = (labelSetting: LabelSettingProps) => {

	const addressType = '';

	return {
		address1: address_1_validation(labelSetting, addressType),
		address2: address_2_validation(labelSetting),
		city: city_validation(labelSetting, addressType),
		state: state_validation(labelSetting, 'country', ''),
		postalCode: postal_code_validation(labelSetting, 'country', '').required(getRequiredText(labelSetting.requiredFormat, labelSetting.postalLabel)),
	};
};

export const InstitutionMailingAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = 'Mailing';

	return {
		billing_address_1: address_1_validation(labelSetting, addressType),
		billing_address_2: address_2_validation(labelSetting),
		billing_address_3: address_3_validation(labelSetting),
		billing_country: country_validation(labelSetting, addressType),
		billing_city: city_validation(labelSetting, addressType),
		billing_state: state_validation(labelSetting, 'billing_country', addressType),
		billing_postal_code: postal_code_validation(labelSetting, 'billing_country', addressType),
	};
};

export const PersonInfoNonEpiscopalMailingAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = 'Mailing';

	return {
		mailing_address_1: validation_nonepiscopal(address_1_validation(labelSetting, addressType), address_3_validation(labelSetting)),
		mailing_address_2: address_2_validation(labelSetting),
		mailing_address_3: address_3_validation(labelSetting),
		mailing_country: validation_nonepiscopal(country_validation(labelSetting, addressType)),
		mailing_city: validation_nonepiscopal(city_validation(labelSetting, addressType)),
		mailing_state: state_validation(labelSetting, 'mailing_country', addressType),
		mailing_postal_code: postal_code_validation(labelSetting, 'mailing_country', addressType),
	};
};

export const InstitutionPhysicalAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = 'Physical';

	return {
		physical_address_1: address_1_validation(labelSetting, addressType),
		physical_address_2: address_2_validation(labelSetting),
		physical_address_3: address_3_validation(labelSetting),
		physical_country: country_validation(labelSetting, addressType),
		physical_city: city_validation(labelSetting, addressType),
		physical_state: state_validation(labelSetting, 'physical_country', addressType),
		physical_postal_code: postal_code_validation(labelSetting, 'physical_country', addressType),
	};
};

export const NewHireMailingAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = 'Mailing';

	return {
		mailing_address_1: address_1_validation(labelSetting, addressType),
		mailing_address_2: address_2_validation(labelSetting),
		mailing_address_3: address_3_validation(labelSetting),
		mailing_country: country_validation(labelSetting, addressType),
		mailing_city: city_validation(labelSetting, addressType),
		mailing_state: state_validation(labelSetting, 'mailing_country', addressType),
		mailing_postal_code: postal_code_validation(labelSetting, 'mailing_country', addressType),
	};
};

export const InstitutionAddRequestMailingAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = 'Mailing';

	return {
		mail_address1: address_1_validation(labelSetting, addressType),
		mail_address2: address_2_validation(labelSetting),
		mail_address3: address_3_validation(labelSetting),
		mail_country: country_validation(labelSetting, addressType),
		mail_city: city_validation(labelSetting, addressType),
		mail_state: state_validation(labelSetting, 'mail_country', addressType),
		mail_postal_code: postal_code_validation(labelSetting, 'mail_country', addressType),
	};
};

export const InstitutionAddRequestPhysicalAddressValidationSchema = (labelSetting: LabelSettingProps, ignore?: boolean) => {
	if (ignore) return {};

	const addressType = 'Physical';

	return {
		street_address1: address_1_validation(labelSetting, addressType),
		street_address2: address_2_validation(labelSetting),
		street_address3: address_3_validation(labelSetting),
		street_country: country_validation(labelSetting, addressType),
		street_city: city_validation(labelSetting, addressType),
		street_state: state_validation(labelSetting, 'street_country', addressType),
		street_postal_code: postal_code_validation(labelSetting, 'street_country', addressType),
	};
};